import React, { useState } from 'react';
import Button from '../../ui/Button';

export interface WifiData {
  ssid: string;
  password: string;
  rotateReminder: boolean;
}

interface WifiSetupStepProps {
  data: WifiData;
  onUpdate: (data: WifiData) => void;
  onNext: () => void;
  onBack: () => void;
}

const inputBase =
  'w-full px-3 py-2.5 border rounded-lg text-sm text-coffee-900 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:border-coffee-400 transition-colors';

const WifiSetupStep: React.FC<WifiSetupStepProps> = ({ data, onUpdate, onNext, onBack }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!data.ssid.trim()) newErrors.ssid = 'Wi-Fi network name is required';
    if (!data.password.trim()) newErrors.password = 'Wi-Fi password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  const update = (field: keyof WifiData, value: string | boolean) => {
    onUpdate({ ...data, [field]: value });
    if (errors[field as string]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">Step 3 of 5</div>
        <h2 className="text-display text-2xl text-coffee-900">Wi-Fi setup</h2>
        <p className="mt-1.5 text-sm text-coffee-600">
          Enter your Wi-Fi credentials. These are shown to guests after they complete your survey.
        </p>
      </div>

      {/* SSID */}
      <div>
        <label htmlFor="wifi-ssid" className="block text-sm font-medium text-coffee-800 mb-1.5">
          Network name (SSID) <span className="text-red-500">*</span>
        </label>
        <input
          id="wifi-ssid"
          type="text"
          value={data.ssid}
          onChange={(e) => update('ssid', e.target.value)}
          placeholder="e.g., JoesCoffee-Guest"
          className={`${inputBase} ${errors.ssid ? 'border-red-400' : 'border-coffee-200'}`}
          aria-invalid={!!errors.ssid}
          aria-describedby={errors.ssid ? 'ssid-error' : undefined}
        />
        {errors.ssid && (
          <p id="ssid-error" className="text-red-500 text-xs mt-1">{errors.ssid}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <label htmlFor="wifi-password" className="block text-sm font-medium text-coffee-800 mb-1.5">
          Password <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            id="wifi-password"
            type={showPassword ? 'text' : 'password'}
            value={data.password}
            onChange={(e) => update('password', e.target.value)}
            placeholder="Enter your Wi-Fi password"
            className={`${inputBase} pr-20 ${errors.password ? 'border-red-400' : 'border-coffee-200'}`}
            aria-invalid={!!errors.password}
            aria-describedby={errors.password ? 'password-error' : undefined}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-coffee-500 hover:text-coffee-800 font-medium transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        {errors.password && (
          <p id="password-error" className="text-red-500 text-xs mt-1">{errors.password}</p>
        )}
      </div>

      {/* Rotate Reminder */}
      <label className="flex items-center gap-3 p-4 rounded-xl border border-coffee-100 bg-white cursor-pointer hover:border-coffee-200 transition-colors">
        <input
          type="checkbox"
          checked={data.rotateReminder}
          onChange={(e) => update('rotateReminder', e.target.checked)}
          className="h-4 w-4 rounded border-coffee-300 text-coffee-600 focus:ring-coffee-500"
        />
        <div>
          <span className="text-sm font-medium text-coffee-900">
            Password rotation reminder
          </span>
          <p className="text-xs text-coffee-500 mt-0.5">
            Get a monthly reminder to rotate your Wi-Fi password for security.
          </p>
        </div>
      </label>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-xs text-amber-800 leading-relaxed">
          Your Wi-Fi password is encrypted at rest and only revealed to guests after they complete your survey.
        </p>
      </div>

      <div className="flex justify-between pt-2 border-t border-coffee-100">
        <Button variant="ghost" onClick={onBack}>Back</Button>
        <Button variant="primary" onClick={handleNext}>Next</Button>
      </div>
    </div>
  );
};

export default WifiSetupStep;
